import { API_ORDER_PREFIX } from '@shared/config/api/api-prefixes';
import { orderBaseApi } from './base';
import {
    OrderDataRequest,
    OrderDataResponse,
    OrdersListDataRequest,
    OrdersListDataResponse,
    OrderDataMessageRequest,
} from './order-types';
import { queryParametersToString } from '@shared/lib/utils/query-parameters-to-string/query-parameters-to-string';
import { FileListItem } from '@shared/model/file';
import { isEqualParameters } from '@shared/lib/utils/is-equal-parameters/is-equal-parameters';

export const orderApi = orderBaseApi.injectEndpoints({
    endpoints: (build) => ({
        ordersList: build.query<OrdersListDataResponse, OrdersListDataRequest>({
            query: (parameters) => `${API_ORDER_PREFIX}${queryParametersToString({ ...parameters })}`,
            providesTags: ['OrdersList'],
        }),
        ordersInfinityList: build.query<OrdersListDataResponse, OrdersListDataRequest>({
            query: (parameters) => `${API_ORDER_PREFIX}${queryParametersToString({ ...parameters })}`,
            serializeQueryArgs: (params) => {
                return `${params.endpointName}_${params.queryArgs.search}`;
            },
            merge: (currentCache, newItems) => {
                if (newItems.skip) {
                    currentCache.items.push(...newItems.items);
                }
            },
            forceRefetch({ currentArg, previousArg }) {
                return !isEqualParameters({ currentParameters: currentArg, previousParameters: previousArg });
            },
        }),
        order: build.query<OrderDataResponse, OrderDataRequest>({
            query: (id) => `${API_ORDER_PREFIX}/${id}`,
            providesTags: ['OrderItem'],
        }),
        completeOrder: build.mutation<OrderDataResponse, OrderDataMessageRequest>({
            query: ({ id, comment }) => {
                return {
                    url: `${API_ORDER_PREFIX}/${id}/complete`,
                    method: 'POST',
                    body: { comment },
                };
            },
            invalidatesTags: ['OrdersList'],
        }),
        rejectOrder: build.mutation<OrderDataResponse, OrderDataMessageRequest>({
            query: ({ id, comment }) => {
                return {
                    url: `${API_ORDER_PREFIX}/${id}/reject`,
                    method: 'POST',
                    body: { comment },
                };
            },
            invalidatesTags: ['OrdersList'],
        }),
        orderFiles: build.query<FileListItem[], OrderDataRequest>({
            query: (id) => `${API_ORDER_PREFIX}/${id}/files`,
            providesTags: ['OrderFiles'],
        }),
    }),
});

export const {
    useLazyOrdersListQuery,
    useLazyOrderQuery,
    useCompleteOrderMutation,
    useRejectOrderMutation,
    usePrefetch: usePrefetchOrder,
    useLazyOrderFilesQuery,
    useLazyOrdersInfinityListQuery,
} = orderApi;
